import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Building2, Users, CreditCard, BarChart3, 
  Settings, LogOut, Menu, Shield, ChevronDown, ChevronRight,
  DollarSign, Activity, Zap, Palette, Globe, FileDown, MessageSquareText,
  Key, HeartPulse, Eye, RefreshCw, Receipt, HardDrive, FileEdit, Package, Play, GitBranch, Target, Trash2, Server, Mail, AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const sidebarItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/super-admin/dashboard' },
  { icon: Building2, label: 'Tenants', path: '/super-admin/tenants' },
  { icon: Package, label: 'Module Control', path: '/super-admin/module-control' },
  { icon: MessageSquareText, label: 'Demo Requests', path: '/super-admin/demo-requests' },
  { icon: Play, label: 'Demo Portals', path: '/super-admin/demo-portals' },
  { icon: GitBranch, label: 'Versions & Flags', path: '/super-admin/versions' },
  { icon: DollarSign, label: 'Plans', path: '/super-admin/plans' },
  { icon: Receipt, label: 'Billing & Invoices', path: '/super-admin/billing' },
  { icon: RefreshCw, label: 'Subscriptions', path: '/super-admin/subscriptions' },
  { icon: CreditCard, label: 'Payments', path: '/super-admin/payments' },
  { icon: Users, label: 'Admin Users', path: '/super-admin/admins' },
  { icon: Eye, label: 'Impersonation Log', path: '/super-admin/impersonation' },
  { icon: Activity, label: 'Activity Logs', path: '/super-admin/activity-logs' },
  { icon: Key, label: 'Settings & Keys', path: '/super-admin/settings' },
  { icon: Mail, label: 'Email Notifications', path: '/super-admin/email' },
  { icon: Shield, label: 'Tenant Safety', path: '/super-admin/tenant-safety' },
  { icon: FileEdit, label: 'Website CMS', path: '/super-admin/cms' },
  { icon: FileEdit, label: 'Blog Posts', path: '/super-admin/blog' },
  { icon: HeartPulse, label: 'System Health', path: '/super-admin/health' },
  { icon: HardDrive, label: 'Database Health', path: '/super-admin/db-health' },
  { icon: AlertTriangle, label: 'Client Errors', path: '/super-admin/client-errors' },
  { icon: Palette, label: 'Themes', path: '/super-admin/themes' },
  { icon: Globe, label: 'Domains', path: '/super-admin/domains' },
  { icon: BarChart3, label: 'Analytics', path: '/super-admin/analytics' },
  { icon: MessageSquareText, label: 'Tenant Support', path: '/super-admin/support' },
  { icon: Target, label: 'Sales CRM', path: '/super-admin/crm' },
  { icon: Trash2, label: 'Launch Cleanup', path: '/super-admin/launch-cleanup' },
  { icon: Server, label: 'Build & Version', path: '/super-admin/version' },
];

export default function SuperAdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem('superAdminUser');
    const token = localStorage.getItem('superAdminToken');
    
    if (!userData || !token) {
      navigate('/super-admin/login');
      return;
    }
    
    setUser(JSON.parse(userData));
    fetchAnalytics(token);
  }, [navigate]);

  const fetchAnalytics = async (token) => {
    try {
      const response = await axios.get(`${API}/platform/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('superAdminToken');
    localStorage.removeItem('superAdminUser');
    toast.success('Logged out successfully');
    navigate('/super-admin/login');
  };

  const closeMobile = () => setIsMobileMenuOpen(false);

  const sidebarInner = (onNavClick) => (
    <>
      <div className="p-5 border-b border-slate-700 bg-gradient-to-br from-slate-800 to-slate-900">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-lg">
            <Shield className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-extrabold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Recruitizr
            </h1>
            <p className="text-xs text-violet-400 uppercase tracking-wider font-semibold">Super Admin</p>
          </div>
        </div>
        {user && (
          <div className="p-2.5 bg-slate-800/50 rounded border border-slate-700">
            <p className="text-sm font-semibold text-white">{user.name}</p>
            <p className="text-xs text-violet-400 uppercase tracking-wider mt-0.5">Platform Admin</p>
          </div>
        )}
      </div>

      {analytics && (
        <div className="p-4 border-b border-slate-700 grid grid-cols-2 gap-2">
          <div className="bg-slate-800/50 rounded p-3 text-center">
            <div className="text-xl font-bold text-white">{analytics.total_tenants}</div>
            <div className="text-xs text-slate-400">Tenants</div>
          </div>
          <div className="bg-slate-800/50 rounded p-3 text-center">
            <div className="text-xl font-bold text-emerald-400">{'\u20B9'}{Number(analytics.revenue_monthly || 0).toLocaleString('en-IN')}</div>
            <div className="text-xs text-slate-400">MRR</div>
          </div>
        </div>
      )}

      <nav className="flex-1 py-4 overflow-y-auto">
        {sidebarItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onNavClick}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                isActive 
                  ? 'bg-violet-600/20 text-violet-400 border-r-4 border-violet-500' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
              }`}
            >
              <Icon size={18} />
              {item.label}
            </Link>
          );
        })}
      </nav>

      <div className="p-4 border-t border-slate-700 space-y-1">
        <Link to="/landing" target="_blank">
          <Button
            variant="ghost"
            className="w-full justify-start gap-3 text-violet-400 hover:text-violet-300 hover:bg-violet-500/10 rounded-sm"
              data-testid="visit-website-btn"
            >
              <Globe size={18} />
              Visit Website
            </Button>
          </Link>
          <Link to="/login">
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 text-slate-500 hover:text-slate-300 hover:bg-slate-800/50 rounded-sm text-xs"
            >
              <Building2 size={16} />
              Go to Tenant Portal
            </Button>
          </Link>
          <Button
            onClick={handleLogout}
            data-testid="super-admin-logout"
            variant="ghost"
            className="w-full justify-start gap-3 text-slate-400 hover:text-white hover:bg-slate-800/50 rounded-sm"
          >
            <LogOut size={18} />
            Logout
          </Button>
        </div>
      </>
    );

  if (!user) return null;

  return (
    <div className="flex min-h-screen bg-slate-950">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col w-64 bg-slate-900 h-screen fixed left-0 top-0 z-40 border-r border-slate-700">
        {sidebarInner(undefined)}
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ml-64">
        {/* Mobile Header */}
        <header className="lg:hidden sticky top-0 z-30 bg-slate-900 border-b border-slate-700 p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="h-6 w-6 text-violet-500" />
            <h1 className="text-lg font-bold text-white">Recruitizr</h1>
          </div>
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-white">
                <Menu size={24} />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0 bg-slate-900 border-slate-700">
              {sidebarInner(closeMobile)}
            </SheetContent>
          </Sheet>
        </header>

        {/* Page Content */}
        <main className="p-6 md:p-8 min-h-screen">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
